<template>
  <div>
    <div
      v-for="(q, i) in questions"
      :key="i"
      class="question-card d-flex justify-content-center"
      :class="{ 'is-flagged': showFlags && q.flagged }"
    >
      <h3 class="question-title">Question {{ i + 1 }}</h3>

      <MultiChoice v-if="q.qType == 'multi-choice'" :content="q.content" :index="i" v-on="$listeners"></MultiChoice>
      <MultiSelect v-if="q.qType == 'multi-select'" :content="q.content" :index="i" v-on="$listeners"></MultiSelect>
      <Dropdown v-if="q.qType == 'dropdown'" :content="q.content" :index="i" v-on="$listeners"></Dropdown>
      <TextAnswer v-if="q.qType == 'text-answer'" :content="q.content" :index="i" v-on="$listeners"></TextAnswer>
      <LinearScale v-if="q.qType == 'linear-scale'" :content="q.content" :index="i" v-on="$listeners"></LinearScale>

      <b-button
        v-if="showFlags && q.flagged"
        class="report-button"
        @click="openReportModal(q)"
        title="Click to report"
        variant="light"
        size="sm"
      >
        <b-icon icon="exclamation-triangle-fill" style="color: #fc7727;" />
      </b-button>
    </div>

    <!-- Report Modal -->
    <b-modal v-model="isReportVisible" id="report-survey" title="Survey Flagged" centered hide-footer>
      <p>Survey Moderation has flagged this question for containing</p>
      <b>{{ flagReason() }}</b>
      <p></p>
      <p>You may submit this survey for a manual review if you disagree with this automated moderation decision.</p>
      <p>Please provide your reason for requesting a manual review:</p>
      <b-form-textarea
        v-model="reportText"
        placeholder="Enter your reason here..."
        rows="5"
        class="mb-3"
      ></b-form-textarea>
      <div class="d-flex justify-content-between">
        <b-button class="btn-alt" @click="closeReportModal">Cancel</b-button>
        <b-button class="btn-default" @click="reportSurvey(reportText)">Submit</b-button>
      </div>
    </b-modal>

    <!-- Add Question -->
    <div class="add-question-div">
      <b-button class="add-question" v-b-modal.add-question-modal>Add Question</b-button>

      <b-modal id="add-question-modal" centered title="Add Question" size="sm">
        <template #modal-header>
          <div class="modal-custom-header">
            <h5 class="modal-custom-title">Add Question</h5>
          </div>
        </template>

        <b-form-group>
          <label
            v-for="type in questionTypes"
            :key="type.value"
            class="radio-option"
            :class="{ 'radio-selected': addQuestionSelect === type.value }"
            :style="addQuestionSelect === type.value ? { borderColor: type.color, background: type.bg } : {}"
          >
            <input
              type="radio"
              v-model="addQuestionSelect"
              name="add-question"
              :value="type.value"
              class="radio-input"
            />
            <span class="radio-icon" :style="{ background: type.color }">{{ type.icon }}</span>
            <span class="radio-label">{{ type.label }}</span>
            <span class="radio-check" v-if="addQuestionSelect === type.value" :style="{ color: type.color }">✓</span>
          </label>
        </b-form-group>

        <template #modal-footer>
          <b-button class="nav-button" :disabled="!addQuestionSelect" @click="addQuestion()">
            Add Question
          </b-button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import MultiChoiceEdit from './edit-survey-components/MultiChoice.vue';
import MultiSelectEdit from './edit-survey-components/MultiSelect.vue';
import DropdownEdit from './edit-survey-components/Dropdown.vue';
import TextAnswerEdit from './edit-survey-components/TextAnswer.vue';
import LinearScaleEdit from './edit-survey-components/LinearScale.vue';

export default {
  name: 'EditQuestions',
  components: {
    MultiChoice: MultiChoiceEdit,
    MultiSelect: MultiSelectEdit,
    Dropdown: DropdownEdit,
    TextAnswer: TextAnswerEdit,
    LinearScale: LinearScaleEdit
  },
  props: {
    questions: Array,
    showFlags: { type: Boolean, default: false }
  },
  data() {
    return {
      addQuestionSelect: null,
      selecetedQuestion: null,
      isReportVisible: false,
      reportText: "",
      questionTypes: [
        { value: 'multi-choice',  label: 'Multiple Choice', icon: '◉', color: '#fc7727', bg: 'rgba(252,119,39,0.07)' },
        { value: 'multi-select',  label: 'Multiple Select', icon: '☑',  color: '#17a2b8', bg: 'rgba(23,162,184,0.07)' },
        { value: 'dropdown',      label: 'Dropdown',        icon: '▾',  color: '#5226DF', bg: 'rgba(82,38,223,0.07)' },
        { value: 'text-answer',   label: 'Text Answer',     icon: '✏',  color: '#DB3360', bg: 'rgba(219,51,96,0.07)' },
        { value: 'linear-scale',  label: 'Linear Scale',    icon: '↔',  color: '#28a745', bg: 'rgba(40,167,69,0.07)' },
      ]
    };
  },
  mounted() {
    this.questions.forEach((q) => {
      this.$watch(
        () => q.content,
        () => { if (q.flagged) q.flagged = false; },
        { deep: true }
      );
    });
  },
  methods: {
    closeModal() {
      this.$bvModal.hide("add-question-modal");
    },
    openReportModal(q) {
      this.isReportVisible = true;
      this.selecetedQuestion = q;
    },
    closeReportModal() {
      this.isReportVisible = false;
      this.reportText = "";
    },
    reportSurvey(reportText) {
      this.$emit("review-request", reportText);
    },
    flagReason() {
      return this.selecetedQuestion ? this.selecetedQuestion.flagReason : "";
    },
    addQuestion() {
      if (!this.addQuestionSelect) return;
      let content = { "title": "" };
      switch (this.addQuestionSelect) {
        case "multi-choice":
          content["options"] = [{ "text": "Option", "imgSrc": "" }];
          break;
        case "multi-select":
          content["options"] = ["Option"];
          break;
        case "dropdown":
          content["options"] = ["Option"];
          break;
        case "text-answer":
          content["imgSrc"] = "";
          break;
        case "linear-scale":
          content["start"] = 1;
          content["end"] = 5;
          content["startLabel"] = "";
          content["endLabel"] = "";
          break;
      }
      this.questions.push({ "qType": this.addQuestionSelect, "content": content });
      this.addQuestionSelect = null;
      this.$bvModal.hide("add-question-modal");
    },
  }
}
</script>

<style scoped>
/* ── Question card ── */
.question-card {
  position: relative;
  flex-direction: column;
  width: min(720px, 94vw);
  margin: 14px auto;
  padding: 20px 22px;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.question-title {
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fc7727;
  margin: 0 0 14px;
}

.is-flagged {
  border-left: 4px solid #fc7727;
  background: rgba(252, 119, 39, 0.04);
}

.report-button {
  position: absolute;
  top: 12px;
  right: 14px;
  background-color: transparent;
  border: none;
  box-shadow: none;
  font-size: 1.25rem;
  padding: 0;
  cursor: pointer;
}

/* ── Child component input overrides ── */
>>> input[type="text"],
>>> input[type="search"],
>>> select,
>>> textarea {
  width: 100%;
  max-width: 640px;
  margin-left: auto;
  display: block;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid #e2e2e2;
  font-size: 13px;
  color: #464444;
  background-color: #fafafa;
  outline: none;
  transition: border-color 0.2s;
}

>>> input[type="text"]:focus,
>>> select:focus,
>>> textarea:focus {
  border-color: #fc7727;
}

>>> .close {
  padding: 0;
  margin-left: auto;
  margin-right: 2px;
  border: none;
  background: none;
  font-size: 24px;
}

>>> .btn {
  margin-top: 8px;
}

/* ── Report modal buttons ── */
.btn-default {
  background-color: #fc7727 !important;
  border: none;
  border-radius: 8px !important;
  font-weight: 700;
  color: #fff !important;
  transition: background 0.2s;
}

.btn-default:hover { background-color: #ff8e4d !important; }

.btn-alt {
  background: #DB3360 !important;
  border: none;
  border-radius: 8px !important;
  font-weight: 700;
  color: #fff !important;
}

.btn-alt:hover { background: #c22c54 !important; }

/* ── Add question button ── */
.add-question-div {
  display: flex;
  justify-content: center;
  margin: 20px 0 28px;
}

.add-question {
  background-color: #fc7727 !important;
  border: none !important;
  color: #fff !important;
  width: min(720px, 94vw);
  height: 48px;
  font-weight: 700 !important;
  font-size: 14px !important;
  border-radius: 10px !important;
  box-shadow: 0 6px 18px rgba(252, 119, 39, 0.3);
  transition: background 0.2s, box-shadow 0.2s !important;
}

.add-question:hover {
  background-color: #ff8e4d !important;
  box-shadow: 0 8px 22px rgba(252, 119, 39, 0.4) !important;
}

.add-question:focus,
.add-question:active { background-color: #fc7727 !important; }

/* ── Custom modal header ── */
.modal-custom-header {
  width: 100%;
  padding: 2px 0;
}

.modal-custom-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #fc7727;
  text-align: center;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Radio option rows ── */
.radio-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  margin-bottom: 8px;
  border: 2px solid #e2e2e2;
  border-radius: 12px;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
  user-select: none;
}

.radio-option:last-child { margin-bottom: 0; }

.radio-option:hover {
  border-color: #fc7727;
  box-shadow: 0 2px 8px rgba(252, 119, 39, 0.1);
}

.radio-selected {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.radio-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.radio-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #fff;
  flex-shrink: 0;
}

.radio-label {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.radio-check {
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
}

/* ── Modal footer add button ── */
.nav-button {
  width: 100%;
  height: 42px;
  background-color: #fc7727 !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(252, 119, 39, 0.28);
  transition: background 0.2s, box-shadow 0.2s !important;
}

.nav-button:hover:not(:disabled) {
  background-color: #ff8e4d !important;
  box-shadow: 0 6px 18px rgba(252, 119, 39, 0.38) !important;
}

.nav-button:disabled {
  background-color: #cccccc !important;
  box-shadow: none !important;
  cursor: not-allowed;
  opacity: 1 !important;
}
</style>


// WEBPACK FOOTER //
// src/components/survey-create/EditQuestions.vue